// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Tile } from '@carbon/react';
import { getStringAttr } from '../../../utils/planAttributes';
import type { HitlApproval } from '../../../types';

export interface LivePlanSummaryProps {
  approval: HitlApproval;
  /** The narration's own prose, when it has been loaded. */
  narrative?: string;
}

/**
 * Plan summary panel: the prose explaining why this plan is running, plus the
 * one control that acts.
 *
 * `Revoke — revert to baseline` lives in the page header instead (see
 * {@link ./LiveRevokeButton}), which is where the mockup puts it.
 */
export default function LivePlanSummary({ approval, narrative }: LivePlanSummaryProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const planPath = `/optimizations/${approval.approvalId}`;
  // Carry the origin so PlanDetail's Back returns to the Live tab rather than
  // depending on history — same contract PlanCard uses.
  const openPlan = (path: string) =>
    navigate(path, { state: { from: `${location.pathname}${location.search}` } });
  // Prefer the narration's drivers narrative — it explains the *why* — and fall
  // back to the approval's own summary attribute.
  const summary = narrative || getStringAttr(approval.attributes, 'summary');

  return (
    <Tile className="live-summary">
      <h3 className="live-summary-title">Plan summary</h3>
      {summary ? (
        <p className="live-summary-body">{summary}</p>
      ) : (
        <p className="live-summary-body live-summary-body--absent">
          This plan carries no summary narration.
        </p>
      )}

      <div className="live-summary-actions">
        <Button kind="tertiary" size="md" onClick={() => openPlan(`${planPath}/audit`)}>
          View audit trail
        </Button>
        <Button kind="tertiary" size="md" onClick={() => openPlan(planPath)}>
          View recommendation
        </Button>
      </div>
    </Tile>
  );
}
